import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  CUSTOM_DARK_ID,
  CUSTOM_LIGHT_ID,
  DEFAULT_SEEDS,
  FALLBACK_THEME,
  SYSTEM_MODE,
  THEMES,
  type ColorScheme,
  type ThemeDef,
  type ThemeMode,
  type ThemeSeeds,
  counterpart,
  findTheme,
  isCustomTheme,
  isValidThemeId,
  normalizeSeeds,
  schemeFromId,
  seedsEqual,
} from '@/config/themes'
import { resolveTokenColors } from '@/utils/cssColor'
import { apiFetch } from '@/api/client'

const STORAGE_KEY = 'ski-admin-theme'
/** Also read by the anti-flash script in `index.html` — keep both in sync. */
const SEEDS_KEY = 'ski-admin-theme-custom'

/** `preferences` as returned by `/api/auth/me` and `/api/auth/profile`. */
export interface ThemePreferences {
  theme: string | null
  themeSeeds: ThemeSeeds | null
}

function systemScheme(): ColorScheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readStored(): ThemeMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === SYSTEM_MODE) return v
    if (isValidThemeId(v) && (findTheme(v) || isCustomTheme(v))) return v
  } catch {
    /* ignore */
  }
  return null
}

function readStoredSeeds(): ThemeSeeds {
  try {
    const raw = localStorage.getItem(SEEDS_KEY)
    if (raw) return normalizeSeeds(JSON.parse(raw))
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_SEEDS }
}

function seedVars(s: ThemeSeeds): Record<string, string> {
  return { '--seed-hue': String(s.hue), '--seed-chroma': String(s.chroma) }
}

export const useThemeStore = defineStore('theme', () => {
  /** User preference — a theme id, or `'system'`. */
  const mode = ref<ThemeMode>(readStored() ?? SYSTEM_MODE)
  /** Tracks `prefers-color-scheme` so `'system'` stays reactive. */
  const osScheme = ref<ColorScheme>(systemScheme())
  /** Seeds for the two custom theme ids. Unused while a preset is active. */
  const seeds = ref<ThemeSeeds>(readStoredSeeds())
  /**
   * True once a signed-in account's preferences have been hydrated. Gates the
   * upstream write so a change made while logged out (login page) is not pushed
   * with no session, and so hydration itself never echoes back.
   */
  const synced = ref(false)

  /** The theme actually painted — `'system'` resolved to a concrete id. */
  const activeTheme = computed(() =>
    mode.value === SYSTEM_MODE ? (osScheme.value === 'dark' ? 'dark' : FALLBACK_THEME) : mode.value,
  )
  const scheme = computed<ColorScheme>(() => schemeFromId(activeTheme.value))
  const isDark = computed(() => scheme.value === 'dark')

  /**
   * Swatch colors for the custom ids. Derived in CSS, so they can only be read
   * back through the probe — recomputed whenever the seeds change.
   */
  const customPreviews = ref<Record<string, [string, string]>>({})

  function refreshCustomPreviews() {
    if (typeof document === 'undefined') return
    const next: Record<string, [string, string]> = {}
    for (const id of [CUSTOM_LIGHT_ID, CUSTOM_DARK_ID]) {
      const c = resolveTokenColors(['--color-background', '--color-primary'], {
        theme: id,
        scheme: schemeFromId(id),
        vars: seedVars(seeds.value),
      })
      next[id] = [c['--color-background'] ?? '#fafafa', c['--color-primary'] ?? '#111111']
    }
    customPreviews.value = next
  }

  /** Presets plus the two seed-driven entries, for the picker. */
  const themes = computed<ThemeDef[]>(() => [
    ...THEMES,
    {
      id: CUSTOM_LIGHT_ID,
      label: '自定义浅色',
      scheme: 'light',
      preview: customPreviews.value[CUSTOM_LIGHT_ID] ?? ['#fafafa', '#111111'],
      builtin: false,
    },
    {
      id: CUSTOM_DARK_ID,
      label: '自定义深色',
      scheme: 'dark',
      preview: customPreviews.value[CUSTOM_DARK_ID] ?? ['#0a0a0a', '#f5f5f5'],
      builtin: false,
    },
  ])

  /**
   * Entries for any theme chooser: the presets plus a leading `'system'` row.
   * That row has no palette of its own, so its swatch borrows whatever is
   * currently painted. Shared so the header picker and the profile page cannot
   * disagree about the list.
   */
  const options = computed(() => [
    {
      id: SYSTEM_MODE,
      label: '跟随系统',
      preview:
        themes.value.find((t) => t.id === activeTheme.value)?.preview ?? themes.value[0].preview,
    },
    ...themes.value.map((t) => ({ id: t.id, label: t.label, preview: t.preview })),
  ])

  /**
   * Seeds go on `documentElement.style` — inline style outranks every selector,
   * so no `!important`, and `removeProperty` restores the preset cleanly.
   */
  function applySeeds(theme: string) {
    const root = document.documentElement
    if (isCustomTheme(theme)) {
      for (const [k, v] of Object.entries(seedVars(seeds.value))) root.style.setProperty(k, v)
    } else {
      root.style.removeProperty('--seed-hue')
      root.style.removeProperty('--seed-chroma')
    }
  }

  function applyDom(theme: string) {
    const root = document.documentElement
    root.setAttribute('data-theme', theme)
    root.setAttribute('data-scheme', schemeFromId(theme))
    applySeeds(theme)

    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) {
      // Presets carry a literal hex; custom palettes are CSS-derived, so read the
      // painted background back through the probe (a raw token value can be an
      // unevaluated `hsl(… calc(…) …)` string).
      const def = findTheme(theme)
      const color =
        def?.preview[0] || resolveTokenColors(['--color-background'])['--color-background'] || '#fafafa'
      meta.setAttribute('content', color)
    }
  }

  function setTheme(next: ThemeMode) {
    mode.value = next
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
    applyDom(activeTheme.value)
    push({ theme: next })
  }

  /**
   * Persists new seeds. Callers are expected to have run the palette through
   * `auditSeeds` first — this does not re-check contrast.
   */
  function setSeeds(next: ThemeSeeds) {
    seeds.value = normalizeSeeds(next)
    try {
      localStorage.setItem(SEEDS_KEY, JSON.stringify(seeds.value))
    } catch {
      /* ignore */
    }
    applySeeds(activeTheme.value)
    refreshCustomPreviews()
    push({ themeSeeds: seeds.value })
  }

  /**
   * Sends a preference patch to `/api/auth/profile`. Fire-and-forget: the theme
   * is already painted and cached in localStorage, so a failed sync degrades to
   * "this device only" rather than blocking the UI.
   */
  function push(patch: Partial<ThemePreferences>) {
    if (!synced.value) return
    apiFetch('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify({ preferences: patch }),
    }).catch(() => {
      /* device-local preference stands */
    })
  }

  /**
   * Adopts the server's preferences after login. Order of authority:
   * explicit server value > whatever localStorage already painted > built-in
   * default. localStorage stays the first-paint authority (the anti-flash script
   * cannot await a request), so the server value is written back into it —
   * localStorage is a cache, the account is the truth.
   *
   * A null `theme` means the account never chose. That is not the same as
   * "choose light": if this device has a local preference we keep painting it and
   * push it upstream, which makes the first login on a fresh account adopt
   * whatever the user had already set anonymously.
   */
  function hydrate(prefs: ThemePreferences | null | undefined) {
    synced.value = true
    if (!prefs) return

    if (prefs.themeSeeds) {
      const next = normalizeSeeds(prefs.themeSeeds)
      if (!seedsEqual(next, seeds.value)) {
        seeds.value = next
        try {
          localStorage.setItem(SEEDS_KEY, JSON.stringify(next))
        } catch {
          /* ignore */
        }
        applySeeds(activeTheme.value)
        refreshCustomPreviews()
      }
    }

    const remote = prefs.theme
    if (remote && (remote === SYSTEM_MODE || isValidThemeId(remote))) {
      if (remote !== mode.value) {
        mode.value = remote
        try {
          localStorage.setItem(STORAGE_KEY, remote)
        } catch {
          /* ignore */
        }
        applyDom(activeTheme.value)
      }
      return
    }

    // Account has no stored theme — adopt this device's choice, if it made one.
    const local = readStored()
    if (local) push({ theme: local })
    if (isCustomTheme(activeTheme.value) && !prefs.themeSeeds) {
      push({ themeSeeds: seeds.value })
    }
  }

  /** Called on logout so the next account's hydration starts clean. */
  function resetSync() {
    synced.value = false
  }

  /** Header quick-toggle: flip light ⇄ dark within the current theme family. */
  function toggle() {
    setTheme(counterpart(activeTheme.value))
  }

  function init() {
    applyDom(activeTheme.value)
    refreshCustomPreviews()

    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener?.('change', () => {
      osScheme.value = systemScheme()
    })
  }

  watch(activeTheme, (v) => applyDom(v))

  return {
    mode,
    activeTheme,
    scheme,
    isDark,
    themes,
    options,
    seeds,
    synced,
    setTheme,
    setSeeds,
    toggle,
    hydrate,
    resetSync,
    init,
  }
})
